import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setLoginWorkflowData,
  setOptInChecked,
  setOptInStatus,
  setPatientCaseId,
  setPatientCaseStatusId,
  setPatientProfileId,
  setPatientUserId,
  setPreferredLanguage,
  setUserName,
} from "../services/PatientStatusSlice";
import { useGetPatientLoginWorkflowQuery } from "../../../auth/patient-auth/PatientOnboardingAPISlice";
import { usePatientFhirUpdateMutation } from "../../../auth/patient-auth/PatientFhirApiSlice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PATIENTCASE_STATUS_ID_MAPPING,
  API_ERROR_STATUS_CODE,
} from "../../../utility/constants/common-constants";
import { useAppSelector } from "../../../store/hooks";
import OptInOptOut from "./OptInOptOut";
import CustomSpinner from "../../../components/custom-spinner/CustomSpinner";
import { patientProfileDetailsActions } from "../../../features/header-component/services/PatientProfileDetailsSlice";
import { getPatientRoute } from "../../../utility/functions/common-functions";
import BannerContent from "../../../features/banner-content/BannerContent";
import { LockIcon } from "../../../assets/icons/LockSvg";
import { useTranslation } from "react-i18next";
import { resetFhirAuthContext } from "../../../auth/patient-auth/FhirAuthProvider";
import { patientAuthSliceActions } from "../../../auth/patient-auth/PatientAuthSlice";
import { getErrorMessageContent } from "./ErrorMessage";
import MainLayout from "../../../layouts/main-layout/MainLayout";

interface PatientStatusCheckerProps {
  children: ReactNode;
}

const PatientStatusChecker: React.FC<PatientStatusCheckerProps> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { accessToken, patientFhirId } = useAppSelector(
    (state) => state.patientAuth
  );
  const optInStatus = useAppSelector(
    (state) => state.patientOnboardingStatus.isOptedIn
  );

  const [authError, setAuthError] = useState(false);
  const [techError, setTechError] = useState(false);
  const [fhirErrorCode, setFhirErrorCode] = useState(0);
  const [techErrorCode, setTechErrorCode] = useState(0);

  // Mutation to update FHIR resource
  const [putPatientFhir, { isLoading: putPatientFhirLoading }] =
    usePatientFhirUpdateMutation();

  // Query for login workflow
  const {
    data,
    isLoading: loginLoading,
    error: loginError,
  } = useGetPatientLoginWorkflowQuery({ accessToken, patientFhirId });

  // Fetch onboarding UI messages based on preferred language
  const patientPreferredLang =
    useAppSelector(
      (state) => state.patientOnboardingStatus.preferredLanguage
    ) ||
    localStorage.getItem("patientPreferredLang") ||
    "en";

  const authorizationErrorMessage = getErrorMessageContent(
    "AuthorizationError",
    patientPreferredLang
  );
  const techErrorMessage = getErrorMessageContent(
    "TechError",
    patientPreferredLang
  );

  // 1) Try updating FHIR; if it returns 401/403, flip authError
  useEffect(() => {
    if (accessToken && patientFhirId) {
      putPatientFhir({ accessToken, patientFhirId })
        .unwrap()
        .catch((err) => {
          if (err && "status" in (err as any)) {
            const status = Number((err as any).status);
            setFhirErrorCode(status);
          }
        });
    }
  }, [accessToken, patientFhirId, putPatientFhir]);

  // 2) Watch for errors from the login workflow query
  useEffect(() => {
    if (loginError && "status" in (loginError as any)) {
      const status = Number((loginError as any).status);
      setTechErrorCode(status);
    }
    const isForbidden = [fhirErrorCode, techErrorCode].includes(
      API_ERROR_STATUS_CODE.FORBIDDEN
    );
    const isBadRequest = techErrorCode === API_ERROR_STATUS_CODE.BAD_REQUEST;

    const unathorised =
      fhirErrorCode === API_ERROR_STATUS_CODE.UNAUTHORIZED ||
      techErrorCode === API_ERROR_STATUS_CODE.UNAUTHORIZED;
    const notFound = techErrorCode === API_ERROR_STATUS_CODE.NOT_FOUND;
    const hasErrorBreakpoint =
      fhirErrorCode > API_ERROR_STATUS_CODE.NOT_FOUND ||
      fhirErrorCode === API_ERROR_STATUS_CODE.ERROR_BREAKPOINT ||
      techErrorCode > API_ERROR_STATUS_CODE.NOT_FOUND ||
      techErrorCode === API_ERROR_STATUS_CODE.ERROR_BREAKPOINT;

    if (isForbidden || isBadRequest || notFound) {
      setAuthError(true);
    } else if (unathorised) {
      dispatch(patientAuthSliceActions.clearUser());
      resetFhirAuthContext();
    } else if (hasErrorBreakpoint) {
      setTechError(true);
    }
  }, [loginError, fhirErrorCode]);

  // 3) Once login data is available, dispatch everything and navigate
  useEffect(() => {
    if (loginLoading || !data) return;

    const {
      patientCaseStatusId,
      patientProfileId,
      patientCaseId,
      userId,
      userName,
      preferredLanguage,
    } = data.loginWorkflowInfo;

    const isOptedIn =
      patientCaseStatusId === PATIENTCASE_STATUS_ID_MAPPING.active;

    dispatch(setLoginWorkflowData(data.loginWorkflowInfo));
    dispatch(setOptInStatus(isOptedIn));
    dispatch(setOptInChecked(true));
    dispatch(setPatientProfileId(patientProfileId));
    dispatch(
      patientProfileDetailsActions.setPatientProfileId(patientProfileId)
    );
    dispatch(setPatientCaseId(patientCaseId));
    dispatch(setPatientCaseStatusId(patientCaseStatusId));
    dispatch(setPatientUserId(userId));
    dispatch(setUserName(userName));
    dispatch(setPreferredLanguage(preferredLanguage));

    const onboardingPath = "/onboarding/user-consent";
    const currentPath = location.pathname;

    if (
      patientCaseStatusId === PATIENTCASE_STATUS_ID_MAPPING.active &&
      currentPath === onboardingPath
    ) {
      navigate(getPatientRoute(patientProfileId, ""));
    }

    if (
      [
        PATIENTCASE_STATUS_ID_MAPPING.enrollmentPending,
        PATIENTCASE_STATUS_ID_MAPPING.inactive,
      ].includes(patientCaseStatusId) &&
      currentPath !== onboardingPath
    ) {
      navigate(onboardingPath);
    }
  }, [data, loginLoading, dispatch, navigate, location.pathname]);

  // Loading spinner while fetching login workflow
  if (loginLoading) {
    return <CustomSpinner />;
  }

  if (techError) {
    return (
      <BannerContent
        html
        title={t("oops!")}
        message={techErrorMessage}
        icon={<LockIcon width="95" height="115" />}
      />
    );
  }
  // If putPatientFhir or query error set authError
  if (authError) {
    return (
      <BannerContent
        html
        title={t("oops!")}
        message={authorizationErrorMessage}
        icon={<LockIcon width="95" height="115" />}
      />
    );
  }
  // Opt-in check
  return optInStatus ? <>{children}</> : <OptInOptOut />;
};

export default PatientStatusChecker;
